import React, { SetStateAction } from "react";
import { ExternalLink, X } from "lucide-react";
import { SiQuickbooks } from "react-icons/si";

export const QuickBooksSetupGuide = ({
  onClose,
  variant = "default",
}: {
  setExpanded?: React.Dispatch<SetStateAction<boolean>>;
  expanded?: boolean;
  onClose?: () => void;
  variant?: "default" | "embedded";
}) => {
  const isEmbedded = variant === "embedded";

  const connectorSteps = [
    {
      title: "Map Environments",
      description: "Map your QuickBooks environment (Production or Sandbox) to your ThriveStack application environment.",
    },
    {
      title: "Connect QuickBooks",
      description: "Click 'Connect with QuickBooks' to authorize ThriveStack via OAuth. You will be redirected to QuickBooks to approve access — no credentials to enter manually.",
    },
    {
      title: "Start Date",
      description: "Select the date from which ThriveStack will begin syncing your QuickBooks data (e.g., 2023-01-01). Earlier dates include more historical revenue.",
    },
    {
      title: "Test Connection",
      description: "Verify the QuickBooks connection before starting data ingestion.",
    },
    {
      title: "Start Ingestion",
      description: "Trigger the data sync from your QuickBooks Online account to ThriveStack.",
    },
  ];

  return (
    <div className="border-0 shadow-none">
      <div className={isEmbedded ? "pb-4" : "pb-6 border-b border-gray-200"}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`shrink-0 p-2 rounded-lg bg-green-50 ${isEmbedded ? "mt-0.5" : ""}`}>
              <SiQuickbooks className={isEmbedded ? "h-5 w-5 text-[#2CA01C]" : "h-6 w-6 text-[#2CA01C]"} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-0.5">
                Setup Guide
              </p>
              <h2 className={isEmbedded ? "text-lg font-bold text-gray-900 leading-tight" : "text-2xl font-bold text-gray-900 leading-tight"}>
                QuickBooks Integration
              </h2>
              <p className={`text-gray-500 mt-1 ${isEmbedded ? "text-xs" : "text-sm"}`}>
                Follow these steps to connect and sync your QuickBooks Online account.
              </p>
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0 mt-1"
              aria-label="Close setup guide"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className={isEmbedded ? "px-1 pb-1 pt-2 space-y-6" : "p-6 space-y-8"}>

        {/* Prerequisites */}
        <div>
          <div className="mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-0.5">Before you begin</p>
            <h3 className={`font-bold text-gray-900 ${isEmbedded ? "text-sm" : "text-base"}`}>Prerequisites</h3>
          </div>
          <div className="space-y-2 ml-7">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border border-gray-300 text-gray-700">Required</span>
              <span className="text-sm">Admin access to your QuickBooks Online account</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border border-gray-300 text-gray-700">Required</span>
              <a
                href="https://developer.intuit.com/app/developer/qbo/docs/develop/authentication-and-authorization/oauth-2.0"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
              >
                OAuth 2.0 credentials (Client ID, Client Secret, Access Token, Refresh Token)
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border border-gray-300 text-gray-700">Required</span>
              <a
                href="https://developer.intuit.com/app/developer/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
              >
                Intuit Developer account with an app created
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Step 1: Set up the connector */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center justify-center w-6 h-6 bg-gray-900 text-white rounded-full text-sm font-bold">1</div>
            <h3 className="text-lg font-semibold">Set up the QuickBooks connector</h3>
          </div>
          <div className="ml-8 space-y-3">
            {connectorSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium mt-0.5 flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <p className="text-sm font-medium">{step.title}</p>
                  <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Sandbox vs Production */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center justify-center w-6 h-6 bg-gray-900 text-white rounded-full text-sm font-bold">2</div>
            <h3 className="text-lg font-semibold">Sandbox vs Production</h3>
          </div>
          <div className="ml-8">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
              <p className="text-xs font-semibold text-amber-900">Choosing the right environment</p>
              <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                <li>
                  <span className="font-semibold">Sandbox</span> — use for testing. Credentials come from the Intuit Developer Portal sandbox keys.
                </li>
                <li>
                  <span className="font-semibold">Production</span> — use for live data. Ensure your app is approved for production access in Intuit.
                </li>
              </ul>
              <p className="text-xs text-amber-700">
                ThriveStack automatically suggests the correct environment based on your app environment. You can override this in the Map Environments step.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
